<template>
  <asc-page-loading-indicator
    :loading="isDataLoading && apiPageFailure.length === 0"
  >
    <div class="asc-power-bi-container">
      <asc-power-bi-template
        :current-tab="currentTab"
        :on-tab-change="handleTabChange"
        :airline-workspaces="airlineWorkspaces"
        :mapped-powerbi-datasets="loadPowerBIDatasets"
        :mapped-powerbi-all-datasets="mappedPowerBIDatasets"
        :mapped-airline-workspaces="mappedAirlineWorkspaces"
        :new-dashboard-value="newDashboard"
        :is-create-dashboard-button-active="isCreateDashboardButtonActive"
        :dashboard-name-error-message="dashboardNameErrorMessage"
        :is-dialog-loading="isDialogLoading"
        :display-create-dashboard-dialog="displayCreateDashboardDialog"
        :dashboard-embed-token="dashboardEmbedToken"
        :is-dashboard-loading="isDashboardLoading"
        :airline-reports="airlineReportsData"
        :display-delete-dialog="displayDeleteDialog"
        :delete-report-name="deleteReportName"
        :delete-report-toast-definition="deleteReportToastDefinition"
        :show-delete-report-toast="showDeleteReportToast"
        :is-report-duplicated="isReportDuplicated"
        :is-readonly-mode="isReadonlyMode"
        :selected-dashboard="selectedDashboard"
        @onCreateDashboard="onCreateDashboard"
        @onCloseCreateDashboardDialog="onCloseCreateDashboardDialog"
        @onCancelCreateDashboardDialog="onCancelCreateDashboardDialog"
        @onCreateNewDashboard="onCreateNewDashboard"
        @handleUpdate="handleUpdate"
        @switchPermissionsSetting="switchSharingPermissions"
        @handleDeleteReport="handleDeleteReport"
        @onCancelDeleteDialog="onCancelDeleteDialog"
        @onDeleteDeleteDialog="onDeleteDeleteDialog"
        @handleUndoDeleteReport="handleUndoDeleteReport"
        @handleDeleteReportToastClose="handleDeleteReportToastClose"
        @onSelectDashboard="onSelectDashboard"
        @removeSelectedDashboard="removeSelectedDashboard"
        @handleSetReadonlyMode="handleSetReadonlyMode"
        @handleCancel="handleCancel"
        @handleSave="handleSave"
        @checkValidName="checkValidName"
        @handleUpdateDashboardName="handleUpdateDashboardName"
        @handleDuplicateReport="handleDuplicateReport"
        @updatePermissions="updatePermissions"
      ></asc-power-bi-template>
      <asc-confirmation
        v-if="showSwitchSharingPermissionsToast"
        :definition="switchSharingPermissionsToastDefinition"
        :on-close="handleCloseSharingPermissionsToast"
      ></asc-confirmation>
      <asc-confirmation
        v-if="isReportDuplicatedCheck"
        :definition="duplicateConfirmationToastDefinition"
        :on-close="handleCloseDuplicateConfirmationToast"
      ></asc-confirmation>
      <asc-confirmation
        v-if="isCollinsReportDuplicated"
        class="collins-ascentia-duplicate"
        :definition="duplicateCollinsWorkspaceConfirmationToastDefinition"
        :on-toast-action="handleConfirmationView"
        :on-close="handleCloseDuplicateCollinsWorkspaceConfirmationToast"
      ></asc-confirmation>
    </div>
    <template v-if="isEditPermissions">
      <asc-dialog
        :show-dialog="isEditPermissions"
        primary-button-text="Apply"
        secondary-button-text="Cancel"
        full-width-buttons
        :on-primary="onPermissionsApply"
        :on-secondary="onPermissionsCancel"
        :on-close="onPermissionsCancel"
        :text="permissionsHeader"
        :width="400"
        class="power-bi-permission-dialog"
      >
        <v-row no-gutters>
          <v-col>
            <v-radio-group
              v-model="isSharedPermission"
              hide-details
              row
              class="permissions-radio-group"
            >
              <v-radio label="Only Me" value="0"></v-radio>
              <v-radio :label="radioAirlineLabel" value="1"></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <p class="permissions-description">
              {{ permissionsBody }}
            </p>
          </v-col>
        </v-row>
      </asc-dialog>
    </template>
  </asc-page-loading-indicator>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import { POWER_BI_CONSTS as PBI, USER_CONSTS as UC } from '@/stores/consts'
import AscPageLoadingIndicator from '../../../molecules/AscPageLoadingIndicator.vue'
import AscPowerBiTemplate from '../../..//templates/AscPowerBiTemplate.vue'
import AppFeatureFlags from '@/utilities/appFeatureFlags'
import AscConfirmation from '../../../molecules/AscConfirmation.vue'
import { ELLIPSES_OPTION } from '@/utilities/appConsts'
import AscDialog from '../../../atoms/AscDialog.vue'
import {
  MINUTES_BEFORE_POWER_BI_TOKEN_EXPIRATION,
  POWER_BI_TOKEN_REFRESH_INTERVAL_TIME,
} from '@/utilities/appConsts'
import _ from 'lodash'

export default {
  name: 'AscPowerBiContainer',

  components: {
    AscPageLoadingIndicator,
    AscPowerBiTemplate,
    AscConfirmation,
    AscDialog,
  },

  beforeRouteLeave(to, from, next) {
    this.removeSelectedDashboard()
    next()
  },

  data() {
    return {
      isEditPermissions: false,
      currentTab: this.getCurrentTabFromLocalStorage(),
      isMyAirlineWorkspaceDataLoaded: false,
      isCollinsAscentiaWorkspaceDataLoaded: false,
      isDialogLoading: false,
      displayCreateDashboardDialog: false,
      isReportDuplicatedCheck: false,
      isCollinsReportDuplicated: false,
      isDashboardDuplicating: false,
      newDashboard: {
        pbiAirline: null,
        pbiDataset: null,
        dashboardName: 'Dashboard 1',
      },
      isLoading: true,
      dashboardLoading: false,
      reportGuid: '',
      showSwitchSharingPermissionsToast: false,
      deleteReportRecord: {},
      deleteReportName: '',
      setIntervalId: null,
      deleteReportIds: [],
      displayDeleteDialog: false,
      showDeleteReportToast: false,
      permissionsHeader: 'Header Not Set',
      permissionsAirline: 'No Airline Set',
      radioAirlineLabel: 'Label Not Set',
      isSharedPermission: '1',
      reportAirline: '',
      switchSharingPermissionsToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: '',
          position: 'bottom',
          dismissible: true,
          hasToastAction: false,
          autoClose: true,
          icon: 'mdi-check',
          duration: 6000,
        },
      ],
      deleteReportToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Dashboard deleted.',
          position: 'bottom',
          dismissible: false,
          hasToastAction: true,
          toastActionLabel: 'Undo',
          autoClose: true,
          icon: 'mdi-delete',
          duration: 12000,
        },
      ],
      duplicateConfirmationToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Dashboard Duplicated',
          position: 'bottom',
          dismissible: true,
          autoClose: true,
          hasToastAction: false,
          icon: 'mdi-check',
          duration: 6000,
        },
      ],
      duplicateCollinsWorkspaceConfirmationToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Dashboard Duplicated to My Airline Workspace',
          position: 'bottom',
          dismissible: true,
          autoClose: true,
          hasToastAction: true,
          toastActionLabel: 'View',
          icon: 'mdi-check',
          duration: 6000,
        },
      ],
      isReadonlyMode: true,
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(PBI.NAMESPACE, [
      'airlineWorkspaces',
      'isValidDashboardName',
      'airlineReports',
      'dashboardEmbedToken',
      'isReportDuplicated',
      'selectedDashboard',
    ]),
    ...mapState(UC.NAMESPACE, ['profile']),
    ...mapGetters(PBI.NAMESPACE, [
      'mappedPowerBIDatasets',
      'mappedAirlineWorkspaces',
    ]),
    isCreateDashboardButtonActive() {
      return (
        (this.mappedAirlineWorkspaces.length === 1
          ? true
          : this.newDashboard.pbiAirline !== null) &&
        this.newDashboard.pbiDataset !== null &&
        this.newDashboard.dashboardName !== '' &&
        this.isValidDashboardName
      )
    },
    permissionsBody() {
      return this.isSharedPermission === '1'
        ? `By selecting this option, all ${this.permissionsAirline} users will be able to see and duplicate this dashboard.`
        : this.isSharedPermission === '0'
        ? 'By selecting this option, only I will be able to see this dashboard.'
        : 'Please Select an Option.'
    },
    airlineReportsData() {
      //ellipses options to display
      let data = this.airlineReports
        .filter(f => !f.isDeleted)
        .map(m => {
          return {
            ...m,
            ellipseOptions: m.isCollinsProvided
              ? ELLIPSES_OPTION.filter(o => o.isCollinsAscentia)
              : ELLIPSES_OPTION.filter(o => !o.isCollinsAscentia || o.isCommon),
          }
        })
      data = this.handleEllipsesOptions(data)
      return data
    },
    isPowerBiPageActive() {
      return AppFeatureFlags.getPowerBiPage()
    },
    dashboardNameErrorMessage() {
      return this.isValidDashboardName &&
        this.selectedDashboard.name !== '' &&
        !(this.selectedDashboard.name?.length > 50)
        ? ''
        : this.selectedDashboard.name === ''
        ? 'This field is required for saving'
        : this.selectedDashboard.name?.length > 50
        ? 'Text exceeding the allowed length'
        : 'This name is already in use.'
    },
    isDataLoading() {
      return this.isLoading || this.isDashboardDuplicating
    },
    loadPowerBIDatasets() {
      return this.mappedPowerBIDatasets.filter(f => {
        let selectedAirlineId =
          this.mappedAirlineWorkspaces.length === 1
            ? this.mappedAirlineWorkspaces[0].id
            : this.newDashboard.pbiAirline?.value
        return f.pbiWorkspaceId === selectedAirlineId
      })
    },
    isDashboardLoading() {
      return this.dashboardLoading
    },
  },

  watch: {
    currentTab(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentTab = newVal
        this.setCurrentTabToLocalStorage(newVal)
        this.loadDataGivenTabIndex()
      }
    },
  },

  mounted() {
    if (this.isPowerBiPageActive) {
      this.initPageData()
    }
  },
  async beforeDestroy() {
    await this.removeSelectedDashboard()
    //Clear the above interval and associated Id on page destruction to prevent multiple loops
    if (this.setIntervalId) {
      clearInterval(this.setIntervalId)
      this.setIntervalId = null
    }
  },

  methods: {
    ...mapMutations(PBI.NAMESPACE, [
      PBI.UPDATE_VALID_DASHBOARD_NAME,
      PBI.UPDATE_DASHBOARD_EMBED_TOKEN,
      PBI.UPDATE_AIRLINE_REPORTS,
      PBI.UPDATE_SELECTED_POWERBI_DASHBOARD,
      PBI.REMOVE_SELECTED_POWERBI_DASHBOARD,
    ]),
    ...mapActions(PBI.NAMESPACE, [
      PBI.GET_AIRLINE_WORKSPACES,
      PBI.GET_POWERBI_DATASET,
      PBI.CREATE_CLONE_POWERBI_DASHBOARD,
      PBI.VALIDATE_POWERBI_DASHBOARD_NAME,
      PBI.GET_AIRLINE_REPORTS,
      PBI.GET_DASHBOARD_EMBED_TOKEN,
      PBI.UPDATE_SHARING_PERMISSIONS,
      PBI.DELETE_POWERBI_REPORT,
      PBI.SET_SELECTED_POWERBI_DASHBOARD,
      PBI.UPDATE_POWERBI_REPORT,
      PBI.CANCEL_CHANGES,
    ]),
    generateUniqueId() {
      const timePart = Date.now().toString(36).slice(-8) // last 8 chars of timestamp
      const randomPart = Math.random().toString(36).slice(2, 3) // 1 random char
      return '_id_' + timePart + randomPart // 13-character ID including prefix
    },
    onPermissionsApply() {
      if (this.isSharedPermission == !this.selectedObject.isShared) {
        this.switchSharingPermissions(this.selectedObject)
      }
      this.isEditPermissions = false
    },
    onPermissionsCancel() {
      this.isEditPermissions = false
    },
    async onCreateDashboard(updateSelectedDashboard) {
      this.isDialogLoading = true
      var updatedDashboard = await this[PBI.CREATE_CLONE_POWERBI_DASHBOARD](
        this.newDashboard,
      )
      this.isDialogLoading = false
      if (this.isValidDashboardName) {
        this.displayCreateDashboardDialog = false
        this.handleUpdate({
          pbiAirline: null,
          pbiDataset: null,
          dashboardName: '',
        })
        updatedDashboard = {
          ...updatedDashboard,
          ellipseOptions: updatedDashboard.isCollinsProvided
            ? ELLIPSES_OPTION.filter(o => o.isCollinsAscentia)
            : updatedDashboard.ownerId === this.profile.userId
            ? ELLIPSES_OPTION.filter(o => !o.isCollinsAscentia || o.isCommon)
            : ELLIPSES_OPTION.filter(
                o =>
                  (o.ownerOnly === false && !o.isCollinsAscentia) || o.isCommon,
              ),
        }
        updateSelectedDashboard(updatedDashboard)
      }
    },
    onCloseCreateDashboardDialog() {
      this[PBI.UPDATE_VALID_DASHBOARD_NAME](true)
      this.displayCreateDashboardDialog = false
      this.handleUpdate({
        pbiAirline: null,
        pbiDataset: null,
        dashboardName: '',
      })
    },
    onCancelCreateDashboardDialog() {
      this[PBI.UPDATE_VALID_DASHBOARD_NAME](true)
      this.displayCreateDashboardDialog = false
      this.handleUpdate({
        pbiAirline: null,
        pbiDataset: null,
        dashboardName: '',
      })
    },
    onCreateNewDashboard() {
      this.newDashboard.dashboardName = 'Dashboard 1'
      if (this.mappedAirlineWorkspaces.length === 1) {
        this.newDashboard.pbiAirline = this.mappedAirlineWorkspaces[0]
      }
      this.displayCreateDashboardDialog = true
    },
    handleUpdate(value) {
      this[PBI.UPDATE_VALID_DASHBOARD_NAME](true)
      this.newDashboard = { ...value }
    },
    updatePermissions(selectedItem) {
      this.isEditPermissions = true
      this.permissionsAirline =
        selectedItem.selectedObject.pbiDatasets[0].pbiWorkspaceName
      this.isSharedPermission = selectedItem.selectedObject.isShared ? '1' : '0'
      this.permissionsHeader = `WHO CAN SEE "${selectedItem.selectedObject.name}" DASHBOARD?`
      this.radioAirlineLabel = `All ${this.permissionsAirline} Users`
      this.selectedObject = selectedItem.selectedObject
    },
    handleDeleteReport(selectedItem) {
      this.deleteReportRecord = { ...selectedItem.selectedObject }
      this.deleteReportName = selectedItem.selectedObject.name
      this.displayDeleteDialog = true
    },
    handleSetReadonlyMode(value) {
      this.isReadonlyMode = value
    },
    onDeleteDeleteDialog() {
      this.displayDeleteDialog = false
      this.deleteReportIds.push(this.deleteReportRecord.pbiReportId)
      let airlineReports = [
        ...this.airlineReports.map(report => {
          return this.deleteReportIds.find(id => id === report.pbiReportId)
            ? { ...report, isDeleted: true }
            : report
        }),
      ]
      this[PBI.UPDATE_AIRLINE_REPORTS](airlineReports)
      this.showDeleteReportToast = true
    },
    onCancelDeleteDialog() {
      this.deleteReportName = {}
      this.deleteReportName = ''
      this.displayDeleteDialog = false
    },
    handleDeleteReportToastClose() {
      this.showDeleteReportToast = false
      this.handleDeleteReportRecords()
    },
    async handleDeleteReportRecords() {
      if (this.deleteReportIds.length) {
        let ids = this.deleteReportIds
        this.deleteReportIds = []
        await this[PBI.DELETE_POWERBI_REPORT](ids)
      }
    },
    async handleUndoDeleteReport() {
      this.showDeleteReportToast = false
      let deleteReportId = this.deleteReportIds.pop()
      let index = this.airlineReports.findIndex(
        i => i.pbiReportId == deleteReportId,
      )
      if (index > -1) {
        this.airlineReports[index].isDeleted = false
        this[PBI.UPDATE_AIRLINE_REPORTS](this.airlineReports)
      }
      this.deleteReportIds = [
        ...this.deleteReportIds.filter(f => f !== deleteReportId),
      ]
      this.deleteReportName = {}
      this.deleteReportName = ''
    },
    async handleDuplicateReport(selectedItem) {
      this.isDashboardDuplicating = true
      const workspaceData = this.airlineWorkspaces.find(
        x => x.id == selectedItem.selectedObject.pbiDatasets[0].pbiWorkspaceId,
      )

      const originalName = selectedItem.selectedObject.name

      // Remove previous unique ID if present
      const uniqueIdPattern = /_id_[0-9a-z]{9}$/
      let baseName = uniqueIdPattern.test(originalName)
        ? originalName.replace(uniqueIdPattern, '')
        : originalName

      // Trim baseName so final string including unique ID is ≤ 50
      const maxBaseLength = 50 - 13 // 50 is char limit, and 13 chars for "_id_" + 9-char unique ID
      if (baseName.length > maxBaseLength) {
        baseName = baseName.slice(0, maxBaseLength)
      }

      const cloneReportRequest = {
        dashboardName: baseName + this.generateUniqueId(),
        workspaceId: selectedItem.selectedObject.pbiDatasets[0].pbiWorkspaceId,
        reportGuid: selectedItem.selectedObject.pbiReportId,
        workspaceGuid: workspaceData.pbiWorkspaceId,
        profileGuid: workspaceData.pbiProfileId,
        isCloneReport: true,
      }
      await this[PBI.CREATE_CLONE_POWERBI_DASHBOARD](cloneReportRequest)
      if (this.isReportDuplicated) {
        this.isDashboardDuplicating = false
        this.isReportDuplicatedCheck =
          this.currentTab === 0 ? true : this.isReportDuplicatedCheck
        this.isCollinsReportDuplicated =
          this.currentTab === 1 ? true : this.isCollinsReportDuplicated
        if (
          selectedItem.isDuplicatedFromDashboardView &&
          this.isReportDuplicatedCheck
        ) {
          let selectCreatedDashboard =
            this.airlineReports[this.airlineReports.length - 1]
          await this.onSelectDashboard(selectCreatedDashboard)
        }
      }
    },
    handleEllipsesOptions(data) {
      let val = [...[], ...data].map(m => {
        if (m.ownerId !== this.profile.userId) {
          let ellipseOptions = _.cloneDeep(m.ellipseOptions)
          let updatedEllipsesOption = ellipseOptions.map(o => {
            if (!o.ownerOnly || o.isCommon) {
              o.disabled = false
            } else {
              o.disabled = true
            }
            return { ...o }
          })
          m.ellipseOptions = updatedEllipsesOption
        }
        return { ...m }
      })
      return val
    },
    async handleConfirmationView() {
      this.isCollinsReportDuplicated = false
      this.handleTabChange(0)
      let selectCreatedDashboard =
        this.airlineReports[this.airlineReports.length - 1]
      await this.onSelectDashboard(selectCreatedDashboard)
    },
    handleCloseDuplicateConfirmationToast() {
      this.isReportDuplicatedCheck = false
    },
    handleCloseDuplicateCollinsWorkspaceConfirmationToast() {
      this.isCollinsReportDuplicated = false
    },
    handleTabChange(whichTab) {
      this.currentTab = whichTab
    },

    getCurrentTabFromLocalStorage() {
      const tabIndex = localStorage.getItem('power_bi_current_tab')
      return tabIndex ? Number(tabIndex) : 0
    },

    setCurrentTabToLocalStorage(tabIndex) {
      localStorage.setItem('power_bi_current_tab', tabIndex)
    },

    async onSelectDashboard(selectedDashboard) {
      if (selectedDashboard) {
        selectedDashboard.ellipseOptions = selectedDashboard.isCollinsProvided
          ? ELLIPSES_OPTION.filter(o => o.isCollinsAscentia)
          : ELLIPSES_OPTION.filter(o => !o.isCollinsAscentia || o.isCommon)
        let data = this.handleEllipsesOptions([...[], selectedDashboard])
        await this[PBI.SET_SELECTED_POWERBI_DASHBOARD](data[0])
        this.dashboardLoading = true
        await this[PBI.GET_DASHBOARD_EMBED_TOKEN](selectedDashboard.id)

        // This starts a loop that scans for new tokens on the refresh timer.
        // By default, this scans every 5 minutes (300000 milliseconds) and refreshes when there's < 10 minutes of validity left.
        this.setIntervalId = setInterval(
          () => this.checkTokenAndUpdate(this.selectedDashboard?.id),
          POWER_BI_TOKEN_REFRESH_INTERVAL_TIME,
        )
      } else {
        await this[PBI.UPDATE_DASHBOARD_EMBED_TOKEN](null)

        //Clear the above interval and associated Id if there is no dashboard to prevent multiple loops
        clearInterval(this.setIntervalId)
        this.setIntervalId = null
      }
      this.dashboardLoading = false
    },
    async removeSelectedDashboard() {
      await this[PBI.REMOVE_SELECTED_POWERBI_DASHBOARD]()
      await this[PBI.UPDATE_DASHBOARD_EMBED_TOKEN](null)
      await this[PBI.UPDATE_VALID_DASHBOARD_NAME](true)
    },
    async checkTokenAndUpdate(currentReportId) {
      // Get the current time
      const currentTime = Date.now()
      const expiration = Date.parse(this.dashboardEmbedToken?.expiration)

      // Time until token expiration in milliseconds
      const timeUntilExpiration = expiration - currentTime
      const timeToUpdate = MINUTES_BEFORE_POWER_BI_TOKEN_EXPIRATION * 60 * 1000

      // Update the token if it is about to expired
      if (timeUntilExpiration <= timeToUpdate) {
        await this[PBI.GET_DASHBOARD_EMBED_TOKEN](currentReportId)
      }
    },
    async initPageData() {
      await this.loadDataGivenTabIndex()
    },
    async loadDataGivenTabIndex() {
      if (this.currentTab === 0 && this.isMyAirlineWorkspaceDataLoaded) {
        return
      }
      if (this.currentTab === 1 && this.isCollinsAscentiaWorkspaceDataLoaded) {
        return
      }
      switch (this.currentTab) {
        case 0:
          await this.loadAirlineWorkspaces()
          this.isMyAirlineWorkspaceDataLoaded = true
          break
        default:
          await this.loadAirlineWorkspaces()
          this.isCollinsAscentiaWorkspaceDataLoaded = true
          break
      }
    },
    async loadAirlineWorkspaces() {
      await Promise.all([
        this[PBI.GET_AIRLINE_WORKSPACES](),
        this[PBI.GET_POWERBI_DATASET](),
        this[PBI.GET_AIRLINE_REPORTS](),
      ])
      this.isLoading = false
    },
    async switchSharingPermissions(pbiReport) {
      this.reportGuid = pbiReport.pbiReportId
      this.reportAirline = pbiReport.pbiDatasets[0].pbiWorkspaceName
      await [this[PBI.UPDATE_SHARING_PERMISSIONS](this.reportGuid)]
      this.setSwitchPermissionsToastText(pbiReport.isShared)
      this.showSwitchSharingPermissionsToast = true
    },
    handleCloseSharingPermissionsToast() {
      this.showSwitchSharingPermissionsToast = false
    },
    setSwitchPermissionsToastText(isSharedStartState) {
      if (isSharedStartState === false) {
        this.switchSharingPermissionsToastDefinition[0].text = `This dashboard is now shared with ${this.reportAirline} Users.`
      } else {
        this.switchSharingPermissionsToastDefinition[0].text =
          'This dashboard is now shared with only myself.'
      }
    },
    handleCancel() {
      this.handleSetReadonlyMode(true)
      this[PBI.CANCEL_CHANGES]()
    },
    async handleSave() {
      await this[PBI.UPDATE_POWERBI_REPORT](this.selectedDashboard)
      this.handleSetReadonlyMode(true)
    },
    async checkValidName(dashboard) {
      await this[PBI.VALIDATE_POWERBI_DASHBOARD_NAME](dashboard)
    },
    handleUpdateDashboardName(value) {
      this[PBI.UPDATE_SELECTED_POWERBI_DASHBOARD]({ name: value })
    },
  },
}
</script>

<style lang="scss">
.asc-power-bi-container {
  background-color: $gray-100;

  & .container {
    padding: 16px;
  }
  .collins-ascentia-duplicate {
    .toast-action {
      text-decoration: underline;
    }
  }
}
</style>
